import { Permission } from 'app/services/api/models/Permission';
import { PermissionType } from 'app/services/api/models/PermissionType';
import { UserRole } from 'app/services/api/models/UserRole';
import { FLEET_EMBED_STORAGE_KEY } from 'app/utils/fleet-embed';

export const DRIVER_PORTAL_URL = '/driver-portal/';

export const ALLOWED_LOGIN_PERMISSIONS: PermissionType[] = [
  PermissionType.PERM_EDIT_EQUIPMENT,
  PermissionType.PERM_IS_SYS_ADMIN,
  PermissionType.PERM_IS_SYS_ROOT,
  PermissionType.PERM_IS_ACCOUNT_ADMIN,
  PermissionType.PERM_IS_USER_ADMIN,
  PermissionType.PERM_VIEW_ALL_TERMINALS,
  PermissionType.PERM_EDIT_USER_PASSWORDS,
  PermissionType.PERM_VIEW_PORTAL_DRIVERS_TAB,
  PermissionType.PERM_VIEW_PORTAL_DRIVER_LOGS_TAB,
  PermissionType.PERM_EDIT_PORTAL_DRIVER_LOGS_TAB,
  PermissionType.PERM_VIEW_PORTAL_VIOLATIONS_TAB,
  PermissionType.PERM_VIEW_PORTAL_DVIR_HISTORY_TAB,
  PermissionType.PERM_VIEW_PORTAL_HOS_REPORTS_TAB,
  PermissionType.PERM_EDIT_ALERTS,
  PermissionType.PERM_EDIT_DRIVERS,
  PermissionType.PERM_VIEW_SUB_ACCOUNTS,
  PermissionType.PERM_SUPPORT_PORTAL_TAB_VIEW,
];

export const PORTAL_ACCESS_PERMISSIONS: PermissionType[] = [
  PermissionType.PERM_IS_SYS_ROOT,
  PermissionType.PERM_IS_SYS_ADMIN,
  PermissionType.PERM_IS_USER_ADMIN,
  PermissionType.PERM_EDIT_EQUIPMENT,
  PermissionType.PERM_VIEW_ALL_USERS,
  PermissionType.PERM_IS_FLEET_MANAGER,
  PermissionType.PERM_IS_ACCOUNT_ADMIN,
  PermissionType.PERM_VIEW_SUB_ACCOUNTS,
  PermissionType.PERM_SUPPORT_PORTAL_TAB_VIEW,
];

function getPermissionNames(permissions: Permission[] = []) {
  return permissions.map(permission => permission.name?.trim());
}

export function hasAllowedLoginPermission(permissions: Permission[] = []) {
  const permissionNames = getPermissionNames(permissions);
  return ALLOWED_LOGIN_PERMISSIONS.some(permission =>
    permissionNames.includes(permission),
  );
}

export function hasPortalAccess(permissions: Permission[] = []) {
  const permissionNames = getPermissionNames(permissions);
  return PORTAL_ACCESS_PERMISSIONS.some(permission =>
    permissionNames.includes(permission),
  );
}

function hasPermission(permissions: Permission[], permission: PermissionType) {
  return getPermissionNames(permissions).includes(permission);
}

/**
 * Drivers with equipment access but no admin roles should use the driver portal.
 * Mirrors vt-eld-canjs-portal login.vm.js driver redirect logic.
 */
export function shouldRedirectToDriverPortal(permissions: Permission[] = []) {
  const isDriver = hasPermission(permissions, PermissionType.PERM_IS_DRIVER);
  const isAssetAdmin = hasPermission(
    permissions,
    PermissionType.PERM_EDIT_EQUIPMENT,
  );
  const isFleetManager = hasPermission(
    permissions,
    PermissionType.PERM_IS_FLEET_MANAGER,
  );
  const isUserAdmin = hasPermission(
    permissions,
    PermissionType.PERM_IS_USER_ADMIN,
  );
  const isAccountAdmin = hasPermission(
    permissions,
    PermissionType.PERM_IS_ACCOUNT_ADMIN,
  );

  return (
    isDriver &&
    isAssetAdmin &&
    !isUserAdmin &&
    !isFleetManager &&
    !isAccountAdmin
  );
}

export function getPermissionsFromUserRoles(
  roleIds: number[] = [],
  roles: UserRole[] = [],
): Permission[] {
  const safeRoleIds = Array.isArray(roleIds) ? roleIds : [];
  const safeRoles = Array.isArray(roles) ? roles : [];
  const selectedRoles = safeRoles.filter(role =>
    safeRoleIds.includes(role.id),
  );

  return selectedRoles.flatMap(role =>
    (role.permissions ?? []).map(permission =>
      typeof permission === 'string'
        ? { name: permission }
        : { name: (permission as Permission).name },
    ),
  );
}

export function formatPermissionList(permissions: PermissionType[] = []) {
  return permissions.join(', ');
}

export type AdminPortalPermissionError = {
  missingLogin: boolean;
  missingPortalAccess: boolean;
  loginPermissions: PermissionType[];
  portalAccessPermissions: PermissionType[];
};

export function getAdminPortalPermissionError(
  permissions: Permission[] = [],
): AdminPortalPermissionError | null {
  const missingLogin = !hasAllowedLoginPermission(permissions);
  const missingPortalAccess = !hasPortalAccess(permissions);

  if (!missingLogin && !missingPortalAccess) {
    return null;
  }

  return {
    missingLogin,
    missingPortalAccess,
    loginPermissions: ALLOWED_LOGIN_PERMISSIONS,
    portalAccessPermissions: PORTAL_ACCESS_PERMISSIONS,
  };
}
/**
 * Builds the driver-portal URL, forwarding iframe SSO `token` and Fleet
 * `isFleet=true` so the driver portal can keep the same embed session.
 */
export function buildDriverPortalRedirectUrl(): string {
  const params = new URLSearchParams();

  try {
    const token =
      window.sessionStorage.getItem('authToken') ||
      window.localStorage.getItem('token');
    if (token) {
      params.set('token', token);
    }
  } catch {
    // Storage may be unavailable; continue without token.
  }

  try {
    if (window.sessionStorage.getItem(FLEET_EMBED_STORAGE_KEY) === 'true') {
      params.set('isFleet', 'true');
    }
  } catch {
    // Storage may be unavailable; continue without isFleet.
  }

  const query = params.toString();
  return query ? `${DRIVER_PORTAL_URL}?${query}` : DRIVER_PORTAL_URL;
}
